import { create } from 'zustand';
import type {
  PluginManifest,
  WorkspaceInfo,
  MembershipStatus,
  MembershipRole,
  ConfigSource,
} from '@vtak/shared';
import type { VertexClaims } from '@/auth/vertex-claims';

/** localStorage key holding the user's last-selected unit id */
const ACTIVE_UNIT_KEY = 'vtak.activeUnit';

/** Membership status for the active unit, or 'none' when unresolved */
export type ActiveMembershipStatus = MembershipStatus | 'none';

interface WorkspaceState {
  /** The unit the user has chosen (persisted per browser) */
  selectedUnitId: string | null;
  /** Tailoring resolved from the server manifest for the active unit */
  workspace: WorkspaceInfo | null;
  /** Caller's membership status for the active unit */
  membershipStatus: ActiveMembershipStatus;
  /** Caller's role in the active unit */
  role: MembershipRole | null;
  /** Whether the caller holds the platform-admin realm role (Velocity super admin) */
  platformAdmin: boolean;
  /** Active unit emblem key (resolved to a patch asset for display) */
  emblem: string | null;
  /** Ancestor short names root→parent (org breadcrumb) */
  orgPath: string[];
  /** Where the active manifest was resolved from */
  configSource: ConfigSource;
  /** Persist the chosen unit and use it for the next config fetch */
  setSelectedUnit: (unitId: string) => void;
  /** Set the caller's role in the active unit (from memberships) */
  setRole: (role: MembershipRole | null) => void;
  /** Flag the caller as a Velocity platform super admin (from realm roles) */
  setPlatformAdmin: (isPlatformAdmin: boolean) => void;
  /** Seed an initial identity from token claims for instant display, before the
   *  server manifest arrives. Never overwrites an already-loaded manifest. */
  seedFromClaims: (claims: VertexClaims) => void;
  /** Mirror the server manifest's workspace fields into the store */
  applyManifest: (manifest: PluginManifest) => void;
}

/** Read the persisted unit selection without throwing in non-browser contexts */
function readStoredUnit(): string | null {
  try {
    return window.localStorage.getItem(ACTIVE_UNIT_KEY);
  } catch {
    return null;
  }
}

/** Persist the unit selection, ignoring storage failures */
function writeStoredUnit(unitId: string): void {
  try {
    window.localStorage.setItem(ACTIVE_UNIT_KEY, unitId);
  } catch {
    // storage unavailable — selection is still held in memory this session
  }
}

/**
 * Canonical synchronous source for the active workspace. Non-React map code
 * reads it via getState(); React components subscribe.
 */
export const useWorkspaceStore = create<WorkspaceState>((set) => ({
  selectedUnitId: readStoredUnit(),
  workspace: null,
  membershipStatus: 'none',
  role: null,
  platformAdmin: false,
  emblem: null,
  orgPath: [],
  configSource: 'live',
  setSelectedUnit: (unitId) => {
    writeStoredUnit(unitId);
    set({ selectedUnitId: unitId });
  },
  setRole: (role) => set({ role }),
  setPlatformAdmin: (isPlatformAdmin) => set({ platformAdmin: isPlatformAdmin }),
  seedFromClaims: (claims) =>
    set((state) => {
      // The authoritative manifest wins; only seed when nothing is loaded yet.
      if (state.workspace || !claims.primaryUnitId || !claims.unitShort) return state;
      return {
        workspace: {
          unitId: claims.primaryUnitId,
          unitName: claims.unitName ?? claims.unitShort,
          shortName: claims.unitShort,
          mapView: { lat: 39, lng: -98, zoom: 4 },
          corePanelFlags: {},
          emblem: undefined,
          orgPath: claims.orgPath,
        },
        role: claims.unitRole,
        orgPath: claims.orgPath,
        membershipStatus: 'approved' as ActiveMembershipStatus,
        selectedUnitId: state.selectedUnitId ?? claims.primaryUnitId,
      };
    }),
  applyManifest: (manifest) =>
    set({
      workspace: manifest.workspace ?? null,
      membershipStatus: manifest.membershipStatus ?? 'none',
      configSource: manifest.configSource ?? 'live',
      emblem: manifest.workspace?.emblem ?? null,
      orgPath: manifest.workspace?.orgPath ?? [],
      selectedUnitId: manifest.workspace?.unitId ?? readStoredUnit(),
    }),
}));
